import { ApolloClient, useApolloClient } from '@apollo/client';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Platform } from 'react-native';
import Purchases, {
  CustomerInfo,
  IntroEligibility,
  INTRO_ELIGIBILITY_STATUS,
  PurchasesError,
  PurchasesOfferings,
} from 'react-native-purchases';

import { ProjectLimitReachedModal } from '@components/MobileOnboarding/ProjectLimitReachedModal';
import {
  PurchaseStatus,
  PurchaseStatusOverlay,
} from '@components/MobileOnboarding/PurchaseStatusOverlay';
import {
  GetMeDocument,
  GetPaywallStatusDocument,
  GetPaywallStatusQuery,
  GetTeamBillingDocument,
  GetTeamBillingQuery,
  ListTeamsDocument,
  ListTeamsQuery,
} from '@graphql/generated';
import useMe from '@hooks/useMe';
import { BillingCycle, PlanName, SubscriptionStatus } from '@root/types';
import {
  ACTIVE_BILLING_STATUSES,
  MOBILE_PLAN_NAME,
  SUBSCRIPTION_STATUS,
} from '@src/constants/constants';

/**
 * RevenueCat entitlement id granted by the team subscription purchase.
 * Single source of truth — screens checking entitlement state must use
 * this instead of hardcoding the id.
 */
export const RC_TEAM_ENTITLEMENT_ID = 'tasktag_personal';

/**
 * checkTrialOrIntroductoryPriceEligibility resolves to a map keyed by product
 * id, never a boolean — treating the (always-truthy) map itself as the
 * eligibility flag would mark every customer eligible regardless of status.
 */
export function isIntroPriceEligible(
  eligibility: { [productId: string]: IntroEligibility },
  productId: string
): boolean {
  return (
    eligibility[productId]?.status ===
    INTRO_ELIGIBILITY_STATUS?.INTRO_ELIGIBILITY_STATUS_ELIGIBLE
  );
}

const INTRO_ELIGIBILITY_OFFERING_NAMES = [
  'monthly_offering',
  'annual_offering',
];

/**
 * checkTrialOrIntroductoryPriceEligibility takes real store product
 * identifiers (SKUs), not the package identifier used elsewhere in this file
 * to look up packages within an offering — passing the package identifier
 * means the eligibility map never has a matching entry. Resolve the actual
 * `product.identifier` for each plan's package instead.
 */
export function getIntroEligibilityProductIds(
  offerings: PurchasesOfferings | null,
  packageIdentifier: string
): string[] {
  if (!offerings) return [];

  const productIds = INTRO_ELIGIBILITY_OFFERING_NAMES.map(
    (offeringName) =>
      offerings.all[offeringName]?.availablePackages.find(
        (p) => p.identifier === packageIdentifier
      )?.product.identifier
  ).filter((id): id is string => Boolean(id));

  return Array.from(new Set(productIds));
}

type RevenueCatContext = {
  customerInfo: CustomerInfo | null;
  managementUrl: string | null;
  newCustomer: boolean;
  subscriptionStatus: SubscriptionStatus;
  loading: boolean;
  offeringsLoading: boolean;
  restoreSubscription: (
    callback?: () => void,
    failCallback?: () => void
  ) => void;
  startSubscription: (
    teamId?: string,
    callback?: () => void,
    failCallback?: () => void
  ) => void;
  offerings: PurchasesOfferings | null;
  currentPlan: typeof MOBILE_PLAN_NAME;
  setCurrentPlan: React.Dispatch<React.SetStateAction<PlanName>>;
  billingCycle: BillingCycle;
  setBillingCycle: React.Dispatch<React.SetStateAction<BillingCycle>>;
  billedAnuallyCost: number | undefined;
  billedAnuallyMonthlyCost: number | undefined;
  billedMonthlyCost: number | undefined;
  annualSavingsPercentage: number | undefined;
};

const revenueCatContext = createContext<RevenueCatContext | undefined>(
  undefined
);

/**
 * Identify the RevenueCat customer as the given TaskTag user so the
 * purchase is attributed to the correct account. Call before
 * startSubscription on iOS.
 */
export async function loginRevenueCat(userId: string) {
  const { customerInfo } = await Purchases.logIn(`tasktag_user_${userId}`);
  return customerInfo;
}

export const PURCHASE_SYNC_MAX_ATTEMPTS = 8;
export const PURCHASE_SYNC_RETRY_DELAY_MS = 1000;
export const PURCHASE_STATUS_DISPLAY_MS = 2000;

// Which single field is ultimately authoritative (paywall flag vs billing
// status vs teamSubscriptionStatus) is pending product/backend confirmation
// — until then any one of the three signals below confirms the purchase.

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Decide whether the backend GraphQL state reflects the just-purchased
 * subscription for the given team. Any one of these confirms it:
 * - getPaywallStatus.isPaywalled === false
 * - the purchased team reporting teamSubscriptionStatus === 'active'
 * - the purchased team's billing details reporting an active/trialing
 *   subscription
 * Team-scoped checks are limited to teamId so a pre-existing subscription on
 * another team cannot mask a still-unprocessed webhook.
 */
export function isSubscriptionStateConfirmed(
  teamId: string,
  paywallData: GetPaywallStatusQuery | undefined,
  teamsData: ListTeamsQuery | undefined,
  billingData: GetTeamBillingQuery | undefined
): boolean {
  if (paywallData?.getPaywallStatus?.isPaywalled === false) return true;

  const teamActive = teamsData?.listTeams?.some(
    (team) => team?.id === teamId && team?.teamSubscriptionStatus === 'active'
  );
  if (teamActive) return true;

  return (
    billingData?.listTeams?.some((team) => {
      if (team?.id !== teamId) return false;
      const details = team?.subscriptionDetails;
      return (
        details?.isSubscriptionActive === true ||
        details?.isSubscriptionOnTrial === true ||
        ACTIVE_BILLING_STATUSES.includes(details?.subscriptionStatus ?? '')
      );
    }) === true
  );
}

/**
 * After a successful iOS purchase the backend billing state is only updated
 * once the RevenueCat webhook lands, so an immediate refetch can still return
 * the stale paywalled state (TA-10171). Poll the gating queries (paywall
 * status, team list, team billing) with network-only fetches until
 * isSubscriptionStateConfirmed passes, then refetch the same documents so any
 * mounted watcher using its own variables also refreshes. Polling uses
 * client.query rather than refetchQueries because refetchQueries({include})
 * only refetches currently-active (mounted) queries and silently skips the
 * rest, leaving nothing to inspect. Each polling fetch also writes to the
 * cache, so observing screens unlock as soon as the webhook is processed.
 *
 * Returns true once the backend state is confirmed, false if it never catches
 * up within maxAttempts so callers can surface a syncing/failure path instead
 * of silently treating the purchase as unlocked.
 */
export async function syncSubscriptionStateAfterPurchase(
  apolloClient: Pick<ApolloClient<object>, 'query' | 'refetchQueries'>,
  teamId: string,
  {
    maxAttempts = PURCHASE_SYNC_MAX_ATTEMPTS,
    retryDelayMs = PURCHASE_SYNC_RETRY_DELAY_MS,
  }: { maxAttempts?: number; retryDelayMs?: number } = {}
): Promise<boolean> {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const [paywallResult, teamsResult, billingResult] = await Promise.all([
        apolloClient.query<GetPaywallStatusQuery>({
          query: GetPaywallStatusDocument,
          fetchPolicy: 'network-only',
        }),
        apolloClient.query<ListTeamsQuery>({
          query: ListTeamsDocument,
          fetchPolicy: 'network-only',
        }),
        apolloClient.query<GetTeamBillingQuery>({
          query: GetTeamBillingDocument,
          fetchPolicy: 'network-only',
        }),
      ]);

      if (
        isSubscriptionStateConfirmed(
          teamId,
          paywallResult.data,
          teamsResult.data,
          billingResult.data
        )
      ) {
        try {
          await apolloClient.refetchQueries({
            include: [
              ListTeamsDocument,
              GetTeamBillingDocument,
              GetPaywallStatusDocument,
              GetMeDocument,
            ],
          });
        } catch (refetchError) {
          // Confirmation already succeeded; the remaining refetches are
          // best-effort cache freshness, not a reason to fail the purchase.
          console.log('error refetching after purchase', refetchError);
        }
        return true;
      }
    } catch (error) {
      // Transient network error mid-poll — keep retrying until maxAttempts.
      console.log('error polling subscription state after purchase', error);
    }

    if (attempt < maxAttempts) {
      await delay(retryDelayMs);
    }
  }
  return false;
}

export const RevenueCatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { Provider } = revenueCatContext;
  const [managementUrl, setManagementUrl] = useState<string | null>(null);
  const [currentPlan, setCurrentPlan] = useState<PlanName>(MOBILE_PLAN_NAME);
  const [newCustomer, setNewCustomer] = useState(true);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus>(SUBSCRIPTION_STATUS.UNSUBSCRIBED);
  const [loading, setLoading] = useState(false);
  // True until the initial Purchases.getOfferings() call settles, so the
  // Subscribe CTA can distinguish "still fetching" (show a spinner) from
  // "fetch finished but no package available" (stay disabled, no spinner).
  const [offeringsLoading, setOfferingsLoading] = useState(true);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);
  const [offerings, setOfferings] = useState<PurchasesOfferings | null>(null);
  const [showReceiptInUseModal, setShowReceiptInUseModal] = useState(false);
  // Drives PurchaseStatusOverlay — feedback between the SubscribeModal
  // closing and the native purchase sheet resolving (TA-10254 QA finding).
  // 'idle' hides the overlay; the other statuses auto-clear back to 'idle'
  // a couple seconds after landing so the hint doesn't linger.
  const [purchaseStatus, setPurchaseStatus] = useState<PurchaseStatus | 'idle'>(
    'idle'
  );
  const apolloClient = useApolloClient();
  const { t } = useTranslation('models');
  const { me } = useMe();

  const getCustomerInfo = async () => {
    const customerInfo = await Purchases.getCustomerInfo();
    setCustomerInfo(customerInfo);
  };

  // here revenue set the identifier to this
  const identifier = RC_TEAM_ENTITLEMENT_ID;

  const getOfferings = async () => {
    try {
      const offerings = await Purchases.getOfferings();
      // Store whatever RevenueCat returns, not just when `current` has
      // packages — named offerings (e.g. monthly_offering) live under `all`
      // and can be populated even when `current` is null, which would
      // otherwise leave the Subscribe CTA permanently blocked.
      setOfferings(offerings);
    } catch (e) {
      console.log({ error: e });
    } finally {
      setOfferingsLoading(false);
    }
  };

  // Tell the user the subscription failed instead of failing silently; show
  // the underlying error message when one is available.
  const showSubscriptionFailedAlert = useCallback(
    (error?: unknown) => {
      // RevenueCat rejects with PurchasesError objects (plain objects, not
      // Error instances), so read the message structurally.
      const errorMessage = (error as PurchasesError | undefined)?.message;
      Alert.alert(
        t('payments.subscriptionFailed.title'),
        errorMessage || t('payments.subscriptionFailed.description')
      );
    },
    [t]
  );
  const restoreSubscription = useCallback(
    async (callback?: () => void, failCallback?: () => void) => {
      try {
        if (me?.id) {
          await loginRevenueCat(me.id);
        }
        const customerInfo = await Purchases.restorePurchases();
        if (customerInfo.entitlements.active.tasktag_personal) {
          callback?.();
        } else {
          showSubscriptionFailedAlert();
          failCallback?.();
        }
      } catch (error) {
        console.log('customerInfo error', error);
        showSubscriptionFailedAlert(error);
        failCallback?.();
      }
    },
    [showSubscriptionFailedAlert, me?.id, loginRevenueCat]
  );
  const startSubscription = useCallback(
    async (
      teamId?: string,
      callback?: () => void,
      failCallback?: () => void
    ) => {
      if (!offerings) {
        console.log('no offerings');
        showSubscriptionFailedAlert();
        failCallback?.();
        return;
      }
      if (!teamId) {
        console.log('no teamID');
        return;
      }

      setLoading(true);
      // The SubscribeModal closes as soon as this fires, so flip the overlay
      // on here — it's the only feedback the user gets until the native
      // purchase sheet resolves (TA-10254 QA finding).
      setPurchaseStatus('pending');

      const offeringName = 'monthly_offering';

      const rcPackage = offerings.all[offeringName]?.availablePackages.find(
        (p) => p.identifier === identifier
      );

      if (!rcPackage) {
        setLoading(false);
        // Internal error, not a user cancellation — the alert covers it, so
        // don't also flash the misleading "cancelled" overlay copy.
        setPurchaseStatus('idle');
        showSubscriptionFailedAlert();
        failCallback?.();
        return;
      }

      try {
        await Purchases.setAttributes({
          teamId: teamId,
        });

        const { customerInfo } = await Purchases.purchasePackage(rcPackage);

        if (
          typeof customerInfo?.entitlements?.active[identifier] !== 'undefined'
        ) {
          // The backend flips billing state via the RevenueCat webhook, which
          // may land after the purchase resolves — poll until GraphQL confirms
          // the subscription instead of trusting a single refetch (TA-10171).
          const confirmed = await syncSubscriptionStateAfterPurchase(
            apolloClient,
            teamId
          );
          setLoading(false);
          if (confirmed) {
            setPurchaseStatus('success');
            callback?.();
          } else {
            console.log('subscription state never confirmed after purchase');
            // Sync timeout, not a user cancellation — the alert covers it.
            setPurchaseStatus('idle');
            showSubscriptionFailedAlert();
            failCallback?.();
          }
        } else {
          setLoading(false);
          // Entitlement missing is an internal/backend anomaly, not a user
          // cancellation — the alert covers it.
          setPurchaseStatus('idle');
          showSubscriptionFailedAlert();
          failCallback?.();
        }
        console.log({ entitlementID: customerInfo.entitlements.active });
      } catch (error) {
        if (
          (error as PurchasesError | undefined)?.message ===
          'There is already another active subscriber using the same receipt.'
        ) {
          // The receipt belongs to another TaskTag account — tell the user to
          // switch accounts instead of failing silently. ProjectLimitReachedModal
          // covers this case on its own, so drop the overlay instead of also
          // flashing "cancelled" over it.
          setPurchaseStatus('idle');
          setShowReceiptInUseModal(true);
          return;
        }
        console.log('error purchasing package', error);
        setLoading(false);
        const userCancelled = !!(error as PurchasesError | undefined)
          ?.userCancelled;
        // "cancelled" overlay copy is reserved for an actual user
        // cancellation; a real purchase failure relies on the alert instead.
        setPurchaseStatus(userCancelled ? 'cancelled' : 'idle');
        // A user-cancelled purchase sheet is not a failure — skip the alert
        // but keep the failCallback so callers can reset their UI state.
        if (!userCancelled) {
          showSubscriptionFailedAlert(error);
        }
        failCallback?.();
      }
    },
    [apolloClient, billingCycle, offerings, showSubscriptionFailedAlert]
  );

  const customerInfoUpdateHandler = useCallback((info: CustomerInfo) => {
    console.log('setting customer info from handler');
    setCustomerInfo(info);
  }, []);

  const annualPlan = useCallback(() => {
    if (!offerings) return;
    const rcPackage = offerings.all['annual_offering']?.availablePackages.find(
      (p) => p.identifier === identifier
    );
    return rcPackage?.product;
  }, [offerings]);

  const monthlyPlan = useCallback(() => {
    if (!offerings) return;
    const rcPackage = offerings.all['monthly_offering']?.availablePackages.find(
      (p) => p.identifier === identifier
    );
    return rcPackage?.product;
  }, [offerings]);

  const billedAnuallyCost = annualPlan()?.price;
  const billedAnuallyMonthlyCost = billedAnuallyCost && billedAnuallyCost / 12;
  const billedMonthlyCost = monthlyPlan()?.price;

  const calculateAnnualSavingPercentage = useCallback(() => {
    if (!billedAnuallyCost || !billedMonthlyCost) {
      return undefined;
    }
    const percentage =
      ((billedMonthlyCost * 12 - billedAnuallyCost) /
        (billedMonthlyCost * 12)) *
      100;
    return Math.trunc(percentage);
  }, [billedAnuallyCost, billedMonthlyCost]);
  const annualSavingsPercentage = calculateAnnualSavingPercentage();

  useEffect(() => {
    if (!customerInfo) return;

    const customerIsNew = customerInfo.allPurchasedProductIdentifiers.some(
      (ident) => ident === MOBILE_PLAN_NAME
    );
    setNewCustomer(customerIsNew);
    if (customerInfo.activeSubscriptions.length < 1) {
      setSubscriptionStatus(SUBSCRIPTION_STATUS.UNSUBSCRIBED);
    } else {
      setSubscriptionStatus(SUBSCRIPTION_STATUS.SUBSCRIBED);
    }
    setManagementUrl(customerInfo.managementURL);
  }, [customerInfo]);

  useEffect(() => {
    if (purchaseStatus === 'idle' || purchaseStatus === 'pending') return;
    const timer = setTimeout(
      () => setPurchaseStatus('idle'),
      PURCHASE_STATUS_DISPLAY_MS
    );
    return () => clearTimeout(timer);
  }, [purchaseStatus]);

  useEffect(() => {
    if (Platform.OS === 'web') return;

    getCustomerInfo();
    getOfferings();

    Purchases.addCustomerInfoUpdateListener(customerInfoUpdateHandler);

    return () => {
      Purchases.removeCustomerInfoUpdateListener(customerInfoUpdateHandler);
    };
  }, [customerInfoUpdateHandler]);

  useEffect(() => {
    if (Platform.OS !== 'ios') return;

    const productIds = getIntroEligibilityProductIds(offerings, identifier);
    if (productIds.length === 0) return;

    const checkTrialOrIntroductoryPriceEligibility = async () => {
      const eligibility =
        await Purchases.checkTrialOrIntroductoryPriceEligibility(productIds);

      if (
        productIds.some((productId) =>
          isIntroPriceEligible(eligibility, productId)
        )
      ) {
        setNewCustomer(true);
      }
    };
    checkTrialOrIntroductoryPriceEligibility();
  }, [offerings]);

  return (
    <Provider
      value={{
        customerInfo,
        managementUrl,
        newCustomer,
        subscriptionStatus,
        loading,
        offeringsLoading,
        startSubscription,
        restoreSubscription,
        offerings,
        currentPlan,
        setCurrentPlan,
        billingCycle,
        setBillingCycle,
        billedAnuallyCost,
        billedAnuallyMonthlyCost,
        billedMonthlyCost,
        annualSavingsPercentage,
      }}>
      {children}
      {showReceiptInUseModal && (
        <ProjectLimitReachedModal
          iconName='CrownOutline'
          description={t('payments.receiptInUse.description')}
          buttonTitle={t('payments.receiptInUse.buttonTitle')}
          onPrimary={() => setShowReceiptInUseModal(false)}
          onDismiss={() => setShowReceiptInUseModal(false)}
        />
      )}
      {purchaseStatus !== 'idle' && (
        <PurchaseStatusOverlay status={purchaseStatus} />
      )}
    </Provider>
  );
};

const useRevenueCat = (): RevenueCatContext => {
  const context = useContext(revenueCatContext);
  if (context === undefined) {
    throw new Error('useRevenueCat must be used within a Provider');
  }
  return context;
};

export default useRevenueCat;
